import React from 'react';
import {useInstance} from 'react-ioc';
import {useNavigate} from 'react-router';
import {CheckOutlined, CloseCircleOutlined, DeleteOutlined, MinusOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Checkbox, Form, Input, Radio, Space} from 'antd';
import {MaskedInput} from 'antd-mask-input';
import cn from 'classnames';
import _ from 'lodash';
import {observer} from 'mobx-react-lite';

import {ENUM_ORDER_DELIVERY, ENUM_ORDER_ORDER_STATUS} from '../../__generated__/global-types';
import {DOC_POLICY, toOrderId} from '../../config/routes';
import {Store} from '../../model/store/Store';
import {IFormOrder} from '../../shared/forms/forms';
import {calc, calcVariables} from '../../shared/graphql/__generated__/calc';
import {createOrder, createOrderVariables} from '../../shared/graphql/__generated__/createOrder';
import {QUERY_CALC} from '../../shared/graphql/calc';
import {MUTATION_CREATE_ORDER} from '../../shared/graphql/order';
import {CurrencyFormat} from '../../shared/helpers/CurrencyFormat';
import {fullUrl} from '../../shared/helpers/fullUrl';
import {phoneRegex} from '../../shared/helpers/regex';

import cls from './Order.module.scss';

const CustomMasked = (props:any) => {
    const {status} = Form.Item.useStatus();
    // eslint-disable-next-line react/jsx-props-no-spreading
    return (<MaskedInput {...props}
            style={{borderRadius: 0}}
            status={status === 'error' ? 'error' : undefined}
            mask={'+7 (000) 000-00-00'}
            placeholder={'+7 (999) 888-77-66'}
            size={'large'}
        />
    );
}

export const Cart = observer(() => {
    const {order} = useInstance(Store)
    const navigate = useNavigate();

    const [delivery, setDelivery] = React.useState<string | null>('self');
    const [agree, setAgree] = React.useState<boolean>(false);
    const [promo, setPromo] = React.useState<string | null>(null);
    const [promoText, setPromoText] = React.useState<string>('');
    const [form] = Form.useForm();

    const [createOrder, {loading}] = useMutation<createOrder,createOrderVariables>(MUTATION_CREATE_ORDER)

    const calc = useQuery<calc, calcVariables>(QUERY_CALC, {
        variables: {
            positions: _.map(order.positions, (item) => ({
                documentId: item.documentId,
                quantity: item.quantity,
                aromaDocumentId: item.aromaDocumentId ?? undefined,
            })),
            promo: promo,
            delivery: delivery || undefined,
        }
    })

    const onFinish = async (input:IFormOrder) => {
        // Проверка на пустую корзину
        if (!order.positions || order.positions.length === 0) {
            return;
        }

        await createOrder({
            variables: {
                data: {
                    // positions
                    positions: _.map(order.positions, (item) => ({
                        documentId: item.documentId,
                        quantity: item.quantity,
                        aromaDocumentId: item.aromaDocumentId ?? undefined,
                    })),

                    // client data
                    first_name: input.first_name,
                    last_name: input.last_name,
                    phone: input.phone,
                    email: input.email,

                    // delivery
                    delivery: delivery === 'ozon' ? ENUM_ORDER_DELIVERY.ozon : ENUM_ORDER_DELIVERY.self,
                    delivery_data: {address: input.delivery_data},

                    // promo
                    promo: promo,
                }
            },
            onCompleted: (res) => {
                if (res.createOrder?.order_status === ENUM_ORDER_ORDER_STATUS.CREATED_1 && res.createOrder?.documentId) {
                    order.clearState()
                    navigate(toOrderId(res.createOrder.documentId))
                }
            }
        });
    }

    const formDisable = loading || !order.positions || order.positions.length === 0

    return (
        <Form form={form} disabled={formDisable} layout={'vertical'} validateTrigger={'onFinish'} onFinish={onFinish} scrollToFirstError={true}>

            <div className={cn(['half', cls.page])}>
                <h1>Ваш заказ</h1>
                <div className={cls.table}>
                    {_.map(order.positions, (item) => (
                        <React.Fragment key={`${item.documentId}:${item.aromaDocumentId ?? ''}`}>
                            <div className={cls.img_wrap}>
                                <div className={cls.img} style={{backgroundImage: `url(${fullUrl(item.url)})`}}/>
                            </div>
                            <div className={cls.description}>
                                <div className={cls.name}>{item.aromaName ? `${item.name} — ${item.aromaName}` : item.name}</div>
                                <div className={cls.control}>
                                    <Space size={8}>
                                        <Button size={'small'} onClick={() => order.minusQuantity(item.documentId, item.aromaDocumentId ?? undefined)}
                                                icon={<MinusOutlined/>} shape={'default'}/>
                                        <span>{item.quantity} шт.</span>
                                        <Button size={'small'} onClick={() => order.plusQuantity(item.documentId, item.aromaDocumentId ?? undefined)}
                                                icon={<PlusOutlined/>} shape={'default'}/>
                                    </Space>
                                    <div>{CurrencyFormat(item.price * item.quantity)} ₽</div>
                                    <div><Button size={'small'} shape={'default'} icon={<DeleteOutlined/>}
                                                 onClick={() => order.removePosition(item.documentId, item.aromaDocumentId ?? undefined)}/></div>

                                </div>
                            </div>

                            <div className={cls.divider}/>
                        </React.Fragment>
                    ))}
                </div>
                <div className={cls.block}>
                    <h2>Данные</h2>
                    <div className={cls.form}>
                    <Form.Item name={'last_name'} label={'Фамилия'} rules={[
                            {required: true, message: 'Заполните, пожлауйста'}
                        ]}>
                            <Input placeholder={'Фамилия'}/>
                        </Form.Item>
                        <Form.Item name={'first_name'} label={'Имя'} rules={[
                            {required: true, message: 'Заполните, пожлауйста'}
                        ]}>
                            <Input placeholder={'Имя'}/>
                        </Form.Item>
                        <Form.Item name={'phone'} label={'Номер телефона'} rules={[
                            {required: true, message: 'Заполните, пожалуйста'},
                            {pattern: phoneRegex, message: 'Не соответствует формату номера телефона'}
                        ]}
                        >
                            <CustomMasked disabled={formDisable}/>
                        </Form.Item>
                        <Form.Item name={'email'} label={'Email'} rules={[
                            {required: true, message: 'Заполните, пожлауйста'},
                            {type: 'email', message: 'Не соответствует формату email'}

                        ]}>
                            <Input placeholder={'name@example.com'}/>
                        </Form.Item>
                    </div>
                </div>

                <div className={cls.block}>
                    <h2>Доставка</h2>
                    <Radio.Group optionType={'button'} value={delivery} onChange={(e) => {
                        setDelivery(e.target.value);
                        form.setFieldsValue({delivery_data: ''});
                    }}
                                 options={[
                                     {value: 'cdek', label: 'СДЭК', disabled: true},
                                     {value: 'ozon', label: 'OZON Доставка'},
                                     {value: 'self', label: 'Самовывоз'},
                                 ]}/>
                    {delivery === 'ozon' && (
                        <>
                            <Form.Item 
                                name={'delivery_data'} 
                                rules={[
                                    {required: true, message: 'Пожалуйста, укажите адрес ПВЗ'}
                                ]}
                                label="Адрес ПВЗ"
                            >
                                <Input placeholder="Введите адрес пункта выдачи заказов"/>
                            </Form.Item>
                            <div style={{color: 'var(--color-gray)', fontSize: '14px', marginTop: '-8px'}}>
                                Доставка не входит в стоимость заказа. Стоимость доставки будет рассчитана отдельно.
                            </div>
                        </>
                    )}
                    {delivery === 'self' && (
                        <Form.Item name={'delivery_data'} rules={[
                            {required: true, message: 'Пожалуйста, выберите'}
                        ]}>
                            <Radio.Group optionType={'default'} value={'self'} options={[
                                {value: 'Москва, Римского-Корсакова, 11', label: 'Москва, Римского-Корсакова, 11',},
                                {value: 'МО, Солнечногорский район, КП «Тихие Зори»', label: 'МО, Солнечногорский район, КП «Тихие Зори»',},
                            ]}/>
                        </Form.Item>
                    )}
                </div>

                <div className={cls.block}>
                    <h2>Промокод</h2>
                    <div className={cls.promo}>
                        <Input
                            prefix={calc.data?.calc.promo_accepted && <CheckOutlined/>}
                            allowClear={calc.data?.calc.promo_accepted!}
                            onClear={() => setPromo(null)}
                            value={promoText}
                            onChange={(e) => setPromoText(e.target.value)}
                        />
                        <Button type={'primary'} onClick={() => setPromo(promoText)}>Применить</Button>

                    </div>
                    {promo && calc.data?.calc.promo_accepted === false &&
                        <div className={'error'}>
                            <CloseCircleOutlined/> Промокод не применен, укажите другой
                        </div>
                    }
                </div>

                <div className={cls.line}>
                    <div className={cls.block}>
                        <Checkbox checked={agree} onChange={() => setAgree(!agree)}>
                            Согласен с <a href={DOC_POLICY} target={'_blank'} rel={'noreferrer'}>политикой обработки персональных данных</a>
                        </Checkbox>
                        <Button disabled={!agree} htmlType={'submit'} type={'primary'} loading={loading}>Оформить заказ</Button>
                    </div>
                    <div className={cls.calc}>
                        {calc.data?.calc.calc_sum !== calc.data?.calc.calc_market_sum ?
                            <>
                                <div>Позиций на сумму</div>
                                <div className={'line-through'}>{CurrencyFormat(calc.data?.calc.calc_market_sum!, 2)} ₽
                                </div>
                                <div>Позиций с учетом скидки</div>
                                <div>{CurrencyFormat(calc.data?.calc.calc_sum!, 2)} ₽</div>
                            </>
                            :
                            <>
                                <div>Позиций на сумму</div>
                                <div>{CurrencyFormat(calc.data?.calc.calc_sum!, 2)} ₽</div>
                            </>
                        }
                        {calc.data?.calc.calc_discount! !== 0 && <>
                            <div>Скидка по промокоду</div>
                            <div>{CurrencyFormat(calc.data?.calc.calc_discount!, 2)} ₽</div>
                        </>}
                        <div>Доставка</div>
                        <div>{delivery === 'ozon' ? '-' : CurrencyFormat(calc.data?.calc.calc_delivery!, 2) + ' ₽'}</div>
                        <div>Итоговая сумма</div>
                        <div className={'bold'}>{CurrencyFormat(calc.data?.calc.calc_total!, 2)} ₽</div>
                    </div>
                </div>

            </div>
        </Form>
    )

})

